import React, { useMemo } from 'react';
import { SelectInput, useQuery } from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import moment from 'moment';

/**
 * Lot picker for the refractometer QC log.
 *
 * The three control columns on that form - Normal, Low and High - each
 * correspond to a calibration supply in inventory: Refractrol Normal,
 * Refractrol Low and Refractrol High. Rather than typing the lot number from
 * the vial, the tech picks from the lots the site actually holds.
 *
 * Picking a lot also fills the two derived cells in that column, from the lot
 * record in inventory:
 *
 *     Manufacturer            <- cal_nor / cal_low / cal_high
 *     Manufacturer expiration <- expiry_date
 *
 * Both are read-only on the form: they describe the vial, so they belong to
 * inventory rather than to whoever is filling in the log.
 *
 * They are written on selection only, never re-derived when an existing log is
 * reopened. A QC log records what the vial said on the day; if someone later
 * corrects the lot in inventory, that must not silently rewrite a signed-off
 * month.
 *
 * The stored value is still the plain lot number, so existing records and the
 * printed log are unaffected.
 */

/** Which supply type feeds each column, and which fields it writes. */
export const REFRACTROL_LEVELS = {
    nor: {
        label: 'Normal',
        lotSource: 'lot_nor',
        manSource: 'man_nor',
        manExpSource: 'man_exp_nor',
        calField: 'cal_nor',
    },
    low: {
        label: 'Low',
        lotSource: 'lot_low',
        manSource: 'man_low',
        manExpSource: 'man_exp_low',
        calField: 'cal_low',
    },
    high: {
        label: 'High',
        lotSource: 'lot_high',
        manSource: 'man_high',
        manExpSource: 'man_exp_high',
        calField: 'cal_high',
    },
};

/**
 * Match a supply type name to a column.
 *
 * Deliberately loose. The catalog name has been spelled a few ways
 * ("Refractrol", "Refractorl"), and matching on the brand plus the level word
 * survives a rename that a hardcoded id would not. "Normal" cannot collide
 * with "Low" or "High", so the three are unambiguous.
 */
const matchesLevel = (supplyType, level) => {
    const name = String(supplyType || '').toLowerCase();
    if (name.indexOf('refract') === -1) { return false; }
    if (level === 'nor') { return name.indexOf('normal') > -1; }
    if (level === 'low') { return name.indexOf('low') > -1; }
    if (level === 'high') { return name.indexOf('high') > -1; }
    return false;
};

const RefractrolLotInput = ({ level, className, disabled, validate }) => {
    const spec = REFRACTROL_LEVELS[level];
    const form = useForm();
    const { values } = useFormState({ subscription: { values: true } });

    // Every calibration lot still holding stock. One query covers all three
    // columns; the rows carry the supply type name and vendor already.
    const { data: lots } = useQuery({
        type: 'getList',
        resource: 'supplylots',
        payload: {
            filter: { category_id: 2, has_stock: 1 },
            sort: { field: 'expiry_date', order: 'ASC' },
            pagination: { page: 1, perPage: 500 },
        },
    });

    const forThisLevel = useMemo(
        () => (lots || []).filter(lot => matchesLevel(lot.supply_type, level)),
        [lots, level]
    );

    const current = values[spec.lotSource];

    const choices = useMemo(() => {
        const list = forThisLevel.map(lot => ({
            id: lot.lot_no,
            name: lot.lot_no,
        }));
        // A log written before this picker existed - or against a lot since
        // used up - must keep showing its lot number rather than blanking it.
        if (current && !list.find(c => String(c.id) === String(current))) {
            list.push({ id: current, name: current });
        }
        return list;
    }, [forThisLevel, current]);

    /** Copy the chosen lot's manufacturer value and expiry into this column. */
    const handleChange = (event) => {
        const picked = event && event.target ? event.target.value : event;
        const lot = forThisLevel.find(l => String(l.lot_no) === String(picked));

        form.batch(() => {
            if (!lot) {
                // Cleared, or a lot no longer in inventory: blank the derived
                // cells rather than leave the previous lot's numbers behind.
                form.change(spec.manSource, '');
                form.change(spec.manExpSource, '');
                return;
            }

            const assigned = lot[spec.calField];
            form.change(
                spec.manSource,
                (assigned === null || assigned === undefined || assigned === '')
                    ? ''
                    : String(assigned)
            );

            form.change(
                spec.manExpSource,
                lot.expiry_date ? moment(lot.expiry_date).format('MM/DD/YYYY') : ''
            );
        });
    };

    return (
        <SelectInput
            source={spec.lotSource}
            label=""
            choices={choices}
            optionText="name"
            optionValue="id"
            onChange={handleChange}
            className={className}
            disabled={disabled}
            validate={validate}
            allowEmpty
            resettable
        />
    );
};

export default RefractrolLotInput;
